<template>
  <div class="course-management">
    <!-- 面包屑导航 -->
    <el-breadcrumb separator="/" class="breadcrumb">
      <el-breadcrumb-item :to="{ path: '/home' }">首页</el-breadcrumb-item>
      <el-breadcrumb-item>课程管理</el-breadcrumb-item>
      <el-breadcrumb-item>课程信息管理</el-breadcrumb-item>
    </el-breadcrumb>

    <!-- 主内容区 -->
    <el-card class="main-card">
      <!-- 搜索和操作区 -->
      <div class="search-and-actions">
        <el-form :inline="true" :model="searchForm" class="search-form">
          <el-form-item label="课程名称">
            <el-input
              v-model="searchForm.courseName"
              placeholder="请输入课程名称"
              clearable
              @keyup.enter.native="handleSearch"
            ></el-input>
          </el-form-item>

          <el-form-item label="讲师">
            <el-select
              v-model="searchForm.lecturerId"
              placeholder="请选择讲师"
              clearable
              @change="handleSearch"
            >
              <el-option
                v-for="lecturer in lecturers"
                :key="lecturer.id"
                :label="lecturer.lecturerName"
                :value="lecturer.id"
              ></el-option>
            </el-select>
          </el-form-item>

          <el-form-item label="一级分类">
            <el-select
              v-model="searchForm.category1Id"
              placeholder="请选择一级分类"
              clearable
              @change="handleCategory1Change"
            >
              <el-option
                v-for="category in category1List"
                :key="category.id"
                :label="category.categoryName"
                :value="category.id"
              ></el-option>
            </el-select>
          </el-form-item>

          <el-form-item label="二级分类" v-if="category2List.length > 0">
            <el-select
              v-model="searchForm.category2Id"
              placeholder="请选择二级分类"
              clearable
              @change="handleCategory2Change"
            >
              <el-option
                v-for="category in category2List"
                :key="category.id"
                :label="category.categoryName"
                :value="category.id"
              ></el-option>
            </el-select>
          </el-form-item>

          <el-form-item label="课程状态">
            <el-select
              v-model="searchForm.status"
              placeholder="请选择状态"
              clearable
              @change="handleSearch"
            >
              <el-option label="正常" value="1"></el-option>
              <el-option label="禁用" value="0"></el-option>
            </el-select>
          </el-form-item>

          <el-form-item label="上架状态">
            <el-select
              v-model="searchForm.isOnline"
              placeholder="请选择上架状态"
              clearable
              @change="handleSearch"
            >
              <el-option label="已上架" value="1"></el-option>
              <el-option label="未上架" value="0"></el-option>
            </el-select>
          </el-form-item>

          <el-form-item>
            <el-button
              type="primary"
              icon="el-icon-search"
              @click="handleSearch"
              >搜索</el-button
            >
            <el-button icon="el-icon-refresh" @click="resetSearch"
              >重置</el-button
            >
          </el-form-item>
        </el-form>

        <div class="action-buttons">
          <el-button
            type="success"
            icon="el-icon-plus"
            @click="showAddDialog"
            v-hasPermission="'course:add'"
          >
            新增课程
          </el-button>
        </div>
      </div>

      <!-- 课程列表 @row-click="handleRowClick" -->
      <el-table
        v-loading="loading"
        :data="courseList"
        border
        style="width: 100%"
        highlight-current-row
      >
        <el-table-column type="index" label="#" width="50"></el-table-column>
        <el-table-column prop="courseName" label="课程名称" min-width="200">
          <template slot-scope="scope">
            <div class="course-name-wrapper">
              <img
                v-if="scope.row.coverImage"
                :src="scope.row.coverImage"
                :alt="scope.row.courseName"
                class="course-cover"
              />
              <span>{{ scope.row.courseName }}</span>
            </div>
          </template>
        </el-table-column>
        <el-table-column
          prop="lecturerName"
          label="讲师"
          width="120"
        ></el-table-column>
        <el-table-column label="分类" width="200">
          <template slot-scope="scope">
            <span>
              {{ scope.row.category1Name }}
              <span v-if="scope.row.category2Name">
                > {{ scope.row.category2Name }}</span
              >
              <span v-if="scope.row.category3Name">
                > {{ scope.row.category3Name }}</span
              >
            </span>
          </template>
        </el-table-column>
        <el-table-column label="价格" width="120">
          <template slot-scope="scope">
            <span v-if="scope.row.isFree === 1" class="free-tag">免费</span>
            <div v-else class="price-wrapper">
              <span class="discount-price"
                >¥{{ scope.row.discountPrice || scope.row.originalPrice }}</span
              >
              <span v-if="scope.row.discountPrice" class="original-price"
                >¥{{ scope.row.originalPrice }}</span
              >
            </div>
          </template>
        </el-table-column>
        <el-table-column
          prop="totalPeriod"
          label="总课时"
          width="80"
        ></el-table-column>
        <el-table-column
          prop="buyCount"
          label="购买人数"
          width="90"
        ></el-table-column>
        <el-table-column
          prop="studyCount"
          label="学习人数"
          width="90"
        ></el-table-column>
        <el-table-column prop="status" label="状态" width="100">
          <template slot-scope="scope">
            <el-tag :type="scope.row.status === 1 ? 'success' : 'danger'">
              {{ scope.row.status === 1 ? "正常" : "禁用" }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column prop="isOnline" label="上架状态" width="100">
          <template slot-scope="scope">
            <el-tag :type="scope.row.isOnline === 1 ? 'primary' : 'info'">
              {{ scope.row.isOnline === 1 ? "已上架" : "未上架" }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column label="操作" width="260" fixed="right">
          <template slot-scope="scope">
            <div class="table-operation-buttons">
              <el-button
                size="mini"
                type="text"
                @click="showCourseDetail(scope.row)"
                icon="el-icon-view"
                class="operation-btn"
              >
                查看
              </el-button>
              <el-button
                size="mini"
                type="primary"
                @click="showEditDialog(scope.row)"
                icon="el-icon-edit"
                v-hasPermission="'course:edit'"
                class="operation-btn"
              >
                编辑
              </el-button>
              <el-button
                size="mini"
                type="danger"
                @click="handleDelete(scope.row.id)"
                icon="el-icon-delete"
                v-hasPermission="'course:delete'"
                class="operation-btn"
              >
                删除
              </el-button>
            </div>
          </template>
        </el-table-column>
      </el-table>

      <!-- 分页 -->
      <el-pagination
        class="pagination"
        background
        :current-page="pagination.pageNum"
        :page-sizes="[10, 20, 30, 50]"
        :page-size="pagination.pageSize"
        :total="pagination.total"
        layout="total, sizes, prev, pager, next, jumper"
        @size-change="handleSizeChange"
        @current-change="handleCurrentChange"
      ></el-pagination>
    </el-card>

    <!-- 新增/编辑课程对话框 -->
    <el-dialog
      :title="dialogTitle"
      :visible.sync="dialogVisible"
      width="80%"
      :close-on-click-modal="false"
      @close="resetDialogForm"
    >
      <el-form
        ref="courseForm"
        :model="courseForm"
        :rules="formRules"
        label-width="120px"
        class="course-form"
      >
        <el-row :gutter="20">
          <el-col :span="12">
            <el-form-item label="课程名称" prop="courseName">
              <el-input
                v-model="courseForm.courseName"
                placeholder="请输入课程名称"
                maxlength="255"
              ></el-input>
            </el-form-item>

            <el-form-item label="讲师" prop="lecturerId">
              <el-select
                v-model="courseForm.lecturerId"
                placeholder="请选择讲师"
                clearable
              >
                <el-option
                  v-for="lecturer in lecturers"
                  :key="lecturer.id"
                  :label="lecturer.lecturerName"
                  :value="lecturer.id"
                ></el-option>
              </el-select>
            </el-form-item>

            <el-form-item label="一级分类" prop="category1Id">
              <el-select
                v-model="courseForm.category1Id"
                placeholder="请选择一级分类"
                @change="handleFormCategory1Change"
              >
                <el-option
                  v-for="category in category1List"
                  :key="category.id"
                  :label="category.categoryName"
                  :value="category.id"
                ></el-option>
              </el-select>
            </el-form-item>

            <el-form-item
              label="二级分类"
              prop="category2Id"
              v-if="formCategory2List.length > 0"
            >
              <el-select
                v-model="courseForm.category2Id"
                placeholder="请选择二级分类"
                @change="handleFormCategory2Change"
                clearable
              >
                <el-option
                  v-for="category in formCategory2List"
                  :key="category.id"
                  :label="category.categoryName"
                  :value="category.id"
                ></el-option>
              </el-select>
            </el-form-item>

            <el-form-item
              label="三级分类"
              prop="category3Id"
              v-if="formCategory3List.length > 0"
            >
              <el-select
                v-model="courseForm.category3Id"
                placeholder="请选择三级分类"
                clearable
              >
                <el-option
                  v-for="category in formCategory3List"
                  :key="category.id"
                  :label="category.categoryName"
                  :value="category.id"
                ></el-option>
              </el-select>
            </el-form-item>

            <el-form-item label="是否免费" prop="isFree">
              <el-radio-group v-model="courseForm.isFree">
                <el-radio :label="1">免费</el-radio>
                <el-radio :label="0">收费</el-radio>
              </el-radio-group>
            </el-form-item>

            <el-form-item
              label="原价(元)"
              prop="originalPrice"
              v-if="courseForm.isFree === 0"
            >
              <el-input
                v-model.number="courseForm.originalPrice"
                placeholder="请输入原价"
                type="number"
                min="0"
                step="0.01"
              ></el-input>
            </el-form-item>

            <el-form-item
              label="优惠价(元)"
              prop="discountPrice"
              v-if="courseForm.isFree === 0"
            >
              <el-input
                v-model.number="courseForm.discountPrice"
                placeholder="请输入优惠价（可为空）"
                type="number"
                min="0"
                step="0.01"
              ></el-input>
            </el-form-item>
          </el-col>

          <el-col :span="12">
            <el-form-item label="课程封面图" prop="coverImage">
              <el-upload
                class="avatar-uploader"
                :action="uploadUrl"
                :show-file-list="false"
                :on-success="handleCoverUploadSuccess"
                :before-upload="beforeCoverUpload"
              >
                <img
                  v-if="courseForm.coverImage"
                  :src="courseForm.coverImage"
                  class="cover-image"
                  :alt="courseForm.courseName || '课程封面'"
                />
                <i v-else class="el-icon-plus avatar-uploader-icon"></i>
              </el-upload>
              <div class="upload-tip">
                建议上传尺寸为800x450的图片，支持JPG、PNG格式
              </div>
            </el-form-item>

            <el-form-item label="状态" prop="status">
              <el-radio-group v-model="courseForm.status">
                <el-radio :label="1">正常</el-radio>
                <el-radio :label="0">禁用</el-radio>
              </el-radio-group>
            </el-form-item>

            <el-form-item label="是否上架" prop="isOnline">
              <el-radio-group v-model="courseForm.isOnline">
                <el-radio :label="1">已上架</el-radio>
                <el-radio :label="0">未上架</el-radio>
              </el-radio-group>
            </el-form-item>

            <el-form-item label="全局排序" prop="sort">
              <el-input
                v-model.number="courseForm.sort"
                placeholder="数值越小越靠前"
                type="number"
              ></el-input>
            </el-form-item>

            <el-form-item label="前端排序" prop="frontendSort">
              <el-input
                v-model.number="courseForm.frontendSort"
                placeholder="页面内显示排序"
                type="number"
              ></el-input>
            </el-form-item>
          </el-col>
          <el-col :span="24">
            <!-- 在模板中替换课程介绍部分 -->
            <el-form-item label="课程介绍" prop="introId">
              <el-tabs v-model="introTab">
                <el-tab-pane label="编辑" name="edit">
                  <quill-editor
                    v-model="courseIntroContent"
                    :options="quillOption"
                    class="quill-editor"
                    style="height: 300px;padding-bottom: 60px;"
                  ></quill-editor>
                </el-tab-pane>
                <el-tab-pane label="预览" name="preview">
                  <div
                    class="intro-preview-content"
                    v-html="courseIntroContent"
                  ></div>
                </el-tab-pane>
              </el-tabs>
            </el-form-item>
          </el-col>
        </el-row>
      </el-form>

      <div slot="footer" class="dialog-footer">
        <el-button @click="dialogVisible = false">取消</el-button>
        <el-button
          type="primary"
          @click="submitCourseForm"
          :loading="submitLoading"
        >
          确定
        </el-button>
      </div>
    </el-dialog>

    <!-- 课程详情对话框 -->
    <el-dialog
      title="课程详情"
      :visible.sync="detailVisible"
      width="90%"
      :close-on-click-modal="false"
      :fullscreen="isDetailFullscreen"
    >
      <div class="course-detail">
        <div class="detail-header">
          <img
            v-if="currentCourse.coverImage"
            :src="currentCourse.coverImage"
            :alt="currentCourse.courseName"
            class="detail-cover"
          />
          <div class="detail-info">
            <h2 class="detail-title">{{ currentCourse.courseName }}</h2>
            <div class="detail-meta">
              <span class="meta-item">
                <i class="el-icon-user"></i>
                讲师：{{ currentCourse.lecturerName }}
              </span>
              <span class="meta-item">
                <i class="el-icon-folder-opened"></i>
                分类：{{ currentCourse.category1Name }}
                <span v-if="currentCourse.category2Name">
                  > {{ currentCourse.category2Name }}</span
                >
                <span v-if="currentCourse.category3Name">
                  > {{ currentCourse.category3Name }}</span
                >
              </span>
              <span class="meta-item">
                <i class="el-icon-clock"></i>
                总课时：{{ currentCourse.totalPeriod }}
              </span>
              <span class="meta-item">
                <i class="el-icon-shopping-cart"></i>
                购买人数：{{ currentCourse.buyCount }}
              </span>
              <span class="meta-item">
                <i class="el-icon-reading"></i>
                学习人数：{{ currentCourse.studyCount }}
              </span>
            </div>
            <div class="detail-price">
              <span v-if="currentCourse.isFree === 1" class="detail-free"
                >免费</span
              >
              <div v-else>
                <span class="detail-discount"
                  >¥{{
                    currentCourse.discountPrice || currentCourse.originalPrice
                  }}</span
                >
                <span v-if="currentCourse.discountPrice" class="detail-original"
                  >¥{{ currentCourse.originalPrice }}</span
                >
              </div>
            </div>
            <div class="detail-status">
              <el-tag :type="currentCourse.status === 1 ? 'success' : 'danger'">
                {{ currentCourse.status === 1 ? "正常" : "禁用" }}
              </el-tag>
              <el-tag
                :type="currentCourse.isOnline === 1 ? 'primary' : 'info'"
                style="margin-left: 10px"
              >
                {{ currentCourse.isOnline === 1 ? "已上架" : "未上架" }}
              </el-tag>
            </div>
          </div>
        </div>

        <div class="detail-content">
          <el-tabs v-model="detailActiveTab">
            <el-tab-pane label="课程介绍" name="intro">
              <div
                class="intro-content"
                v-html="currentCourse.introContent"
              ></div>
            </el-tab-pane>

            <el-tab-pane label="章节内容" name="chapters">
              <div class="chapters-management">
                <el-button
                  type="success"
                  icon="el-icon-plus"
                  @click="addChapter"
                  v-hasPermission="'courseChapter:add'"
                >
                  添加章节
                </el-button>

                <div class="chapters-list">
                  <div
                    v-for="(chapter, chapterIndex) in chapters"
                    :key="chapter.id"
                    class="chapter-item"
                  >
                    <div class="chapter-header">
                      <div class="chapter-title">
                        <i class="el-icon-folder"></i>
                        <span
                          >第{{ chapterIndex + 1 }}章：{{
                            chapter.chapterName
                          }}</span
                        >
                      </div>
                      <div class="chapter-actions">
                        <el-button
                          size="mini"
                          type="text"
                          @click="editChapter(chapter)"
                          v-hasPermission="'courseChapter:edit'"
                        >
                          编辑
                        </el-button>
                        <el-button
                          size="mini"
                          type="text"
                          @click="addChapterContent(chapter.id)"
                          v-hasPermission="'courseChapterContent:add'"
                        >
                          添加内容
                        </el-button>
                        <el-button
                          size="mini"
                          type="text"
                          @click="deleteChapter(chapter.id)"
                          v-hasPermission="'courseChapter:delete'"
                        >
                          删除
                        </el-button>
                      </div>
                    </div>

                    <div class="chapter-contents">
                      <div
                        v-for="(content, contentIndex) in chapter.contents"
                        :key="content.id"
                        class="content-item"
                      >
                        <div class="content-icon">
                          <i
                            class="el-icon-video-camera"
                            v-if="content.contentType === 2"
                          ></i>
                          <i
                            class="el-icon-microphone"
                            v-if="content.contentType === 3"
                          ></i>
                          <i
                            class="el-icon-document"
                            v-if="content.contentType === 1"
                          ></i>
                        </div>
                        <div class="content-info">
                          <div class="content-title">
                            {{ chapterIndex + 1 }}.{{ contentIndex + 1 }}
                            {{ content.contentTitle }}
                          </div>
                          <div class="content-meta">
                            <span
                              v-if="content.contentType === 2"
                              class="meta-text"
                            >
                              视频时长：{{
                                formatDuration(content.videoDuration)
                              }}
                            </span>
                            <span
                              v-if="content.contentType === 3"
                              class="meta-text"
                            >
                              音频时长：{{
                                formatDuration(content.audioDuration)
                              }}
                            </span>
                            <span v-if="content.isFree === 1" class="free-badge"
                              >免费</span
                            >
                          </div>
                        </div>
                        <div class="content-actions">
                          <el-button
                            size="mini"
                            type="text"
                            @click="editChapterContent(content)"
                            v-hasPermission="'courseChapterContent:edit'"
                          >
                            编辑
                          </el-button>
                          <el-button
                            size="mini"
                            type="text"
                            @click="deleteChapterContent(content.id)"
                            v-hasPermission="'courseChapterContent:delete'"
                          >
                            删除
                          </el-button>
                        </div>
                      </div>

                      <div
                        v-if="chapter.contents.length === 0"
                        class="no-content"
                      >
                        <i class="el-icon-info"></i>
                        <span>该章节暂无内容，请点击"添加内容"按钮</span>
                      </div>
                    </div>
                  </div>

                  <div v-if="chapters.length === 0" class="no-chapter">
                    <i class="el-icon-info"></i>
                    <span>该课程暂无章节，请点击"添加章节"按钮</span>
                  </div>
                </div>
              </div>
            </el-tab-pane>
          </el-tabs>
        </div>
      </div>

      <div slot="footer" class="dialog-footer">
        <el-button @click="detailVisible = false">关闭</el-button>
        <el-button type="primary" @click="toggleDetailFullscreen">
          {{ isDetailFullscreen ? "退出全屏" : "全屏查看" }}
        </el-button>
      </div>
    </el-dialog>

    <!-- 章节编辑对话框 (简化版，实际项目中可能需要更完整的实现) -->
    <el-dialog
      :title="chapterDialogTitle"
      :visible.sync="chapterDialogVisible"
      width="60%"
      :close-on-click-modal="false"
    >
      <!-- 章节编辑表单内容 -->
      <el-form
        ref="chapterForm"
        :model="chapterForm"
        :rules="chapterFormRules"
        label-width="120px"
      >
        <el-form-item label="章节名称" prop="chapterName">
          <el-input
            v-model="chapterForm.chapterName"
            placeholder="请输入章节名称"
          ></el-input>
        </el-form-item>

        <el-form-item label="章节描述">
          <el-input
            v-model="chapterForm.description"
            placeholder="请输入章节描述（可选）"
            type="textarea"
            rows="3"
          ></el-input>
        </el-form-item>

        <el-form-item label="是否免费" prop="isFree">
          <el-radio-group v-model="chapterForm.isFree">
            <el-radio :label="1">免费</el-radio>
            <el-radio :label="0">收费</el-radio>
          </el-radio-group>
        </el-form-item>

        <el-form-item
          label="原价(元)"
          prop="originalPrice"
          v-if="chapterForm.isFree === 0"
        >
          <el-input
            v-model.number="chapterForm.originalPrice"
            placeholder="请输入原价"
            type="number"
          ></el-input>
        </el-form-item>

        <el-form-item
          label="优惠价(元)"
          prop="discountPrice"
          v-if="chapterForm.isFree === 0"
        >
          <el-input
            v-model.number="chapterForm.discountPrice"
            placeholder="请输入优惠价（可为空）"
            type="number"
          ></el-input>
        </el-form-item>

        <el-form-item label="排序权重" prop="sort">
          <el-input
            v-model.number="chapterForm.sort"
            placeholder="数值越小越靠前"
            type="number"
          ></el-input>
        </el-form-item>
      </el-form>

      <div slot="footer" class="dialog-footer">
        <el-button @click="chapterDialogVisible = false">取消</el-button>
        <el-button
          type="primary"
          @click="submitChapterForm"
          :loading="chapterSubmitLoading"
        >
          确定
        </el-button>
      </div>
    </el-dialog>

    <!-- 章节内容编辑对话框 (简化版) -->
    <el-dialog
      :title="contentDialogTitle"
      :visible.sync="contentDialogVisible"
      width="60%"
      :close-on-click-modal="false"
    >
      <!-- 章节内容编辑表单 -->
      <el-form
        ref="contentForm"
        :model="contentForm"
        :rules="contentFormRules"
        label-width="120px"
      >
        <el-form-item label="内容类型" prop="contentType">
          <el-radio-group
            v-model="contentForm.contentType"
            @change="handleContentTypeChange"
          >
            <el-radio :label="1">纯文本</el-radio>
            <el-radio :label="2">视频</el-radio>
            <el-radio :label="3">音频</el-radio>
          </el-radio-group>
        </el-form-item>

        <el-form-item label="内容标题" prop="contentTitle">
          <el-input
            v-model="contentForm.contentTitle"
            placeholder="请输入内容标题"
          ></el-input>
        </el-form-item>

        <el-form-item
          label="纯文本内容"
          prop="textContent"
          v-if="contentForm.contentType === 1"
        >
          <el-input
            v-model="contentForm.textContent"
            placeholder="请输入文本内容"
            type="textarea"
            rows="6"
          ></el-input>
        </el-form-item>

        <div v-if="contentForm.contentType === 2">
          <el-form-item label="视频上传">
            <el-upload
              class="video-uploader"
              :action="videoUploadUrl"
              :show-file-list="false"
              :on-success="handleVideoUploadSuccess"
              :before-upload="beforeVideoUpload"
            >
              <video
                v-if="contentForm.videoUrl"
                :src="contentForm.videoUrl"
                controls
                class="video-preview"
                :alt="contentForm.contentTitle"
              ></video>
              <div v-else class="upload-placeholder">
                <i class="el-icon-video-camera"></i>
                <span>点击上传视频</span>
              </div>
            </el-upload>
          </el-form-item>

          <el-form-item label="视频时长(秒)" prop="videoDuration">
            <el-input
              v-model.number="contentForm.videoDuration"
              placeholder="请输入视频时长"
              type="number"
              min="0"
            ></el-input>
          </el-form-item>
        </div>

        <div v-if="contentForm.contentType === 3">
          <el-form-item label="音频上传">
            <el-upload
              class="audio-uploader"
              :action="audioUploadUrl"
              :show-file-list="false"
              :on-success="handleAudioUploadSuccess"
              :before-upload="beforeAudioUpload"
            >
              <audio
                v-if="contentForm.audioUrl"
                :src="contentForm.audioUrl"
                controls
                class="audio-preview"
                :alt="contentForm.contentTitle"
              ></audio>
              <div v-else class="upload-placeholder">
                <i class="el-icon-microphone"></i>
                <span>点击上传音频</span>
              </div>
            </el-upload>
          </el-form-item>

          <el-form-item label="音频时长(秒)" prop="audioDuration">
            <el-input
              v-model.number="contentForm.audioDuration"
              placeholder="请输入音频时长"
              type="number"
              min="0"
            ></el-input>
          </el-form-item>
        </div>

        <el-form-item label="排序权重" prop="sort">
          <el-input
            v-model.number="contentForm.sort"
            placeholder="数值越小越靠前"
            type="number"
          ></el-input>
        </el-form-item>
      </el-form>

      <div slot="footer" class="dialog-footer">
        <el-button @click="contentDialogVisible = false">取消</el-button>
        <el-button
          type="primary"
          @click="submitContentForm"
          :loading="contentSubmitLoading"
        >
          确定
        </el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import { quillEditor } from "vue-quill-editor";
import "quill/dist/quill.core.css";
import "quill/dist/quill.snow.css";
import "quill/dist/quill.bubble.css";
export default {
  name: "CourseManagement",
  components: {
    quillEditor,
  },
  data() {
    return {
      // 新增富文本编辑器相关变量（关键修正）
      introTab: "edit", // 用于切换编辑/预览标签
      courseIntroContent: "", // 富文本编辑器内容
      // 添加富文本编辑器配置
      quillOption: {
        modules: {
          toolbar: [
            ["bold", "italic", "underline", "strike"],
            ["blockquote", "code-block"],
            [{ header: 1 }, { header: 2 }],
            [{ list: "ordered" }, { list: "bullet" }],
            [{ script: "sub" }, { script: "super" }],
            [{ indent: "-1" }, { indent: "+1" }],
            [{ direction: "rtl" }],
            [{ size: ["small", false, "large", "huge"] }],
            [{ header: [1, 2, 3, 4, 5, 6, false] }],
            [{ color: [] }, { background: [] }],
            [{ font: [] }],
            [{ align: [] }],
            ["clean"],
            ["link", "image", "video"],
          ],
        },
        placeholder: "请输入课程介绍内容...",
      },
      // 加载状态
      loading: false,
      submitLoading: false,
      chapterSubmitLoading: false,
      contentSubmitLoading: false,

      // 搜索表单
      searchForm: {
        courseName: "",
        lecturerId: "",
        category1Id: "",
        category2Id: "",
        status: "",
        isOnline: "",
      },

      // 分页信息
      pagination: {
        pageNum: 1,
        pageSize: 10,
        total: 0,
      },

      // 课程列表数据
      courseList: [],

      // 讲师列表
      lecturers: [],

      // 分类列表
      category1List: [],
      category2List: [],
      category3List: [],

      // 表单分类列表（用于新增/编辑课程）
      formCategory2List: [],
      formCategory3List: [],

      // 对话框状态
      dialogVisible: false,
      dialogTitle: "新增课程",
      detailVisible: false,
      chapterDialogVisible: false,
      contentDialogVisible: false,
      chapterDialogTitle: "新增章节",
      contentDialogTitle: "新增内容",

      // 课程详情相关
      currentCourse: {},
      chapters: [],
      detailActiveTab: "intro",
      isDetailFullscreen: false,

      // 表单数据
      courseForm: {
        id: "",
        courseName: "",
        lecturerId: "",
        category1Id: "",
        category2Id: "",
        category3Id: "",
        coverImage: "",
        introId: "",
        introPreview: "",
        isFree: 0,
        originalPrice: 0,
        discountPrice: null,
        status: 1,
        isOnline: 0,
        sort: 0,
        frontendSort: 0,
      },

      chapterForm: {
        id: "",
        courseId: "",
        chapterName: "",
        description: "",
        isFree: 0,
        originalPrice: null,
        discountPrice: null,
        sort: 0,
      },

      contentForm: {
        id: "",
        chapterId: "",
        contentType: 1,
        contentTitle: "",
        textContent: "",
        videoUrl: "",
        videoDuration: 0,
        audioUrl: "",
        audioDuration: 0,
        sort: 0,
      },

      // 表单验证规则
      formRules: {
        courseName: [
          { required: true, message: "请输入课程名称", trigger: "blur" },
          { max: 255, message: "课程名称不能超过255个字符", trigger: "blur" },
        ],
        lecturerId: [
          { required: true, message: "请选择讲师", trigger: "change" },
        ],
        category1Id: [
          { required: true, message: "请选择一级分类", trigger: "change" },
        ],
        isFree: [
          { required: true, message: "请选择是否免费", trigger: "change" },
        ],
        originalPrice: [
          { required: true, message: "请输入原价", trigger: "blur" },
          {
            type: "number",
            min: 0,
            message: "原价不能为负数",
            trigger: "blur",
          },
        ],
        sort: [
          { required: true, message: "请输入排序权重", trigger: "blur" },
          { type: "number", message: "请输入数字", trigger: "blur" },
        ],
        frontendSort: [
          { required: true, message: "请输入前端排序", trigger: "blur" },
          { type: "number", message: "请输入数字", trigger: "blur" },
        ],
        introId: [
          { required: true, message: "请编辑课程介绍", trigger: "blur" },
        ],
      },

      chapterFormRules: {
        chapterName: [
          { required: true, message: "请输入章节名称", trigger: "blur" },
        ],
        isFree: [
          { required: true, message: "请选择是否免费", trigger: "change" },
        ],
        originalPrice: [
          { required: true, message: "请输入原价", trigger: "blur" },
          {
            type: "number",
            min: 0,
            message: "原价不能为负数",
            trigger: "blur",
          },
        ],
        sort: [
          { required: true, message: "请输入排序权重", trigger: "blur" },
          { type: "number", message: "请输入数字", trigger: "blur" },
        ],
      },

      contentFormRules: {
        contentType: [
          { required: true, message: "请选择内容类型", trigger: "change" },
        ],
        contentTitle: [
          { required: true, message: "请输入内容标题", trigger: "blur" },
        ],
        textContent: [
          { required: true, message: "请输入文本内容", trigger: "blur" },
        ],
        videoUrl: [
          { required: true, message: "请上传视频", trigger: "change" },
        ],
        videoDuration: [
          { required: true, message: "请输入视频时长", trigger: "blur" },
          {
            type: "number",
            min: 0,
            message: "时长不能为负数",
            trigger: "blur",
          },
        ],
        audioUrl: [
          { required: true, message: "请上传音频", trigger: "change" },
        ],
        audioDuration: [
          { required: true, message: "请输入音频时长", trigger: "blur" },
          {
            type: "number",
            min: 0,
            message: "时长不能为负数",
            trigger: "blur",
          },
        ],
        sort: [
          { required: true, message: "请输入排序权重", trigger: "blur" },
          { type: "number", message: "请输入数字", trigger: "blur" },
        ],
      },

      // 上传相关配置
      uploadUrl: "/api/upload/image",
      videoUploadUrl: "/api/upload/video",
      audioUploadUrl: "/api/upload/audio",
    };
  },

  created() {
    this.loadCourses();
    this.loadLecturers();
    this.loadCategory1();
  },

  methods: {
    // 加载课程列表
    async loadCourses() {
      this.loading = true;
      try {
        const params = {
          pageNum: this.pagination.pageNum,
          pageSize: this.pagination.pageSize,
          ...this.searchForm,
        };

        const { data: res } = await this.$http.get("/course/list", { params });

        if (res.code === 200) {
          this.courseList = res.data.rows;
          this.pagination.total = res.data.total;
        } else {
          this.$message.error("获取课程列表失败：" + res.msg);
        }
      } catch (error) {
        this.$message.error("获取课程列表失败，请重试");
        console.error(error);
      } finally {
        this.loading = false;
      }
    },

    // 加载讲师列表
    async loadLecturers() {
      try {
        const { data: res } = await this.$http.get("/lecturer/listAll");

        if (res.code === 200) {
          this.lecturers = res.data;
        } else {
          this.$message.error("获取讲师列表失败：" + res.msg);
        }
      } catch (error) {
        this.$message.error("获取讲师列表失败，请重试");
        console.error(error);
      }
    },

    // 加载一级分类
    async loadCategory1() {
      try {
        const { data: res } = await this.$http.get(
          "/courseCategory/listByLevel",
          {
            params: { level: 1, categoryType: 1 },
          }
        );

        if (res.code === 200) {
          this.category1List = res.data;
        } else {
          this.$message.error("获取一级分类失败：" + res.msg);
        }
      } catch (error) {
        this.$message.error("获取一级分类失败，请重试");
        console.error(error);
      }
    },

    // 加载二级分类
    async loadCategory2(parentId, isForm = false) {
      try {
        const { data: res } = await this.$http.get(
          "/courseCategory/listByParentId",
          {
            params: { parentId, categoryType: 1 },
          }
        );

        if (res.code === 200) {
          if (isForm) {
            this.formCategory2List = res.data;
          } else {
            this.category2List = res.data;
          }
        } else {
          this.$message.error("获取二级分类失败：" + res.msg);
        }
      } catch (error) {
        this.$message.error("获取二级分类失败，请重试");
        console.error(error);
      }
    },

    // 加载三级分类
    async loadCategory3(parentId, isForm = false) {
      try {
        const { data: res } = await this.$http.get(
          "/courseCategory/listByParentId",
          {
            params: { parentId, categoryType: 1 },
          }
        );

        if (res.code === 200) {
          if (isForm) {
            this.formCategory3List = res.data;
          } else {
            this.category3List = res.data;
          }
        } else {
          this.$message.error("获取三级分类失败：" + res.msg);
        }
      } catch (error) {
        this.$message.error("获取三级分类失败，请重试");
        console.error(error);
      }
    },

    // 搜索课程
    handleSearch() {
      this.pagination.pageNum = 1;
      this.loadCourses();
    },

    // 重置搜索
    resetSearch() {
      this.searchForm = {
        courseName: "",
        lecturerId: "",
        category1Id: "",
        category2Id: "",
        status: "",
        isOnline: "",
      };
      this.category2List = [];
      this.category3List = [];
      this.pagination.pageNum = 1;
      this.loadCourses();
    },

    // 分页大小改变
    handleSizeChange(pageSize) {
      this.pagination.pageSize = pageSize;
      this.loadCourses();
    },

    // 当前页改变
    handleCurrentChange(pageNum) {
      this.pagination.pageNum = pageNum;
      this.loadCourses();
    },

    // 一级分类改变（搜索区）
    handleCategory1Change(category1Id) {
      this.searchForm.category2Id = "";
      this.category3List = [];

      if (category1Id) {
        this.loadCategory2(category1Id);
      } else {
        this.category2List = [];
      }

      this.handleSearch();
    },

    // 二级分类改变（搜索区）
    handleCategory2Change(category2Id) {
      if (category2Id) {
        this.loadCategory3(category2Id);
      } else {
        this.category3List = [];
      }

      this.handleSearch();
    },

    // 一级分类改变（表单）
    handleFormCategory1Change(category1Id) {
      this.courseForm.category2Id = "";
      this.courseForm.category3Id = "";
      this.formCategory3List = [];

      if (category1Id) {
        this.loadCategory2(category1Id, true);
      } else {
        this.formCategory2List = [];
      }
    },

    // 二级分类改变（表单）
    handleFormCategory2Change(category2Id) {
      this.courseForm.category3Id = "";

      if (category2Id) {
        this.loadCategory3(category2Id, true);
      } else {
        this.formCategory3List = [];
      }
    },

    // 显示新增课程对话框
    showAddDialog() {
      this.dialogTitle = "新增课程";
      this.resetDialogForm();
      this.dialogVisible = true;
    },

    // 显示编辑课程对话框
    showEditDialog(course) {
      this.dialogTitle = "编辑课程";
      this.resetDialogForm();

      // 复制课程数据到表单
      this.courseForm = { ...course };

      // 加载对应分类
      if (this.courseForm.category1Id) {
        this.loadCategory2(this.courseForm.category1Id, true).then(() => {
          if (this.courseForm.category2Id) {
            this.loadCategory3(this.courseForm.category2Id, true);
          }
        });
      }

      // 加载课程介绍预览
      this.loadCourseIntroPreview();

      this.dialogVisible = true;
    },

    // 重置对话框表单
    resetDialogForm() {
      if (this.$refs.courseForm) {
        this.$refs.courseForm.resetFields();
      }

      this.courseForm = {
        id: "",
        courseName: "",
        lecturerId: "",
        category1Id: "",
        category2Id: "",
        category3Id: "",
        coverImage: "",
        introId: "",
        introPreview: "",
        isFree: 0,
        originalPrice: 0,
        discountPrice: null,
        status: 1,
        isOnline: 0,
        sort: 0,
        frontendSort: 0,
      };

      this.formCategory2List = [];
      this.formCategory3List = [];
    },

    // 提交课程表单
    async submitCourseForm() {
      this.$refs.courseForm.validate(async (valid) => {
        if (!valid) return;

        this.submitLoading = true;

        try {
          let res;

          if (this.courseForm.id) {
            // 编辑课程
            res = await this.$http.put(
              `/course/update/${this.courseForm.id}`,
              this.courseForm
            );
          } else {
            // 新增课程
            res = await this.$http.post("/course/add", this.courseForm);
          }

          if (res.data.code === 200) {
            this.$message.success(
              this.courseForm.id ? "课程更新成功" : "课程新增成功"
            );
            this.dialogVisible = false;
            this.loadCourses();
          } else {
            this.$message.error(
              res.data.msg ||
                (this.courseForm.id ? "课程更新失败" : "课程新增失败")
            );
          }
        } catch (error) {
          this.$message.error(
            this.courseForm.id ? "课程更新失败，请重试" : "课程新增失败，请重试"
          );
          console.error(error);
        } finally {
          this.submitLoading = false;
        }
      });
    },

    // 处理课程封面上传成功
    handleCoverUploadSuccess(response) {
      if (response.code === 200) {
        this.courseForm.coverImage = response.data.url;
        this.$message.success("封面上传成功");
      } else {
        this.$message.error("封面上传失败：" + response.msg);
      }
    },

    // 封面上传前校验
    beforeCoverUpload(file) {
      const isJPG = file.type === "image/jpeg" || file.type === "image/png";
      const isLt2M = file.size / 1024 / 1024 < 2;

      if (!isJPG) {
        this.$message.error("上传图片只能是 JPG/PNG 格式!");
      }
      if (!isLt2M) {
        this.$message.error("上传图片大小不能超过 2MB!");
      }

      return isJPG && isLt2M;
    },

    // 打开课程介绍编辑器
    openCourseIntroEditor() {
      // 在实际项目中，这里会打开一个富文本编辑器
      // 简化处理，假设已经编辑并保存了课程介绍
      this.$message.success("课程介绍编辑功能将在这里实现");

      // 模拟保存后的结果
      this.courseForm.introId = this.courseForm.introId || new Date().getTime();
      this.courseForm.introPreview = "这是课程介绍的预览内容...";
    },

    // 加载课程介绍预览
    async loadCourseIntroPreview() {
      if (this.courseForm.introId) {
        try {
          const { data: res } = await this.$http.get(
            `/courseIntro/${this.courseForm.introId}`
          );

          if (res.code === 200) {
            this.courseForm.introPreview = res.data.content;
          } else {
            this.$message.error("获取课程介绍失败：" + res.msg);
          }
        } catch (error) {
          console.error("获取课程介绍失败", error);
        }
      }
    },

    // 显示课程详情
    async showCourseDetail(course) {
      this.currentCourse = { ...course };
      this.detailVisible = true;
      this.detailActiveTab = "intro";

      // 加载课程介绍详情
      await this.loadCourseIntroDetail();

      // 加载章节和内容
      await this.loadChaptersAndContents();
    },

    // 加载课程介绍详情
    async loadCourseIntroDetail() {
      if (this.currentCourse.introId) {
        try {
          const { data: res } = await this.$http.get(
            `/courseIntro/${this.currentCourse.introId}`
          );

          if (res.code === 200) {
            this.currentCourse.introContent = res.data.content;
          } else {
            this.$message.error("获取课程介绍详情失败：" + res.msg);
            this.currentCourse.introContent = "无法加载课程介绍";
          }
        } catch (error) {
          console.error("获取课程介绍详情失败", error);
          this.currentCourse.introContent = "无法加载课程介绍";
        }
      }
    },

    // 加载章节和内容
    async loadChaptersAndContents() {
      try {
        const { data: res } = await this.$http.get(
          `/courseChapter/listByCourseId`,
          {
            params: { courseId: this.currentCourse.id },
          }
        );

        if (res.code === 200) {
          this.chapters = res.data;

          // 为每个章节加载内容
          for (const chapter of this.chapters) {
            await this.loadChapterContents(chapter);
          }
        } else {
          this.$message.error("获取章节列表失败：" + res.msg);
          this.chapters = [];
        }
      } catch (error) {
        console.error("获取章节列表失败", error);
        this.chapters = [];
        this.$message.error("获取章节列表失败，请重试");
      }
    },

    // 加载章节内容
    async loadChapterContents(chapter) {
      try {
        const { data: res } = await this.$http.get(
          `/courseChapterContent/listByChapterId`,
          {
            params: { chapterId: chapter.id },
          }
        );

        if (res.code === 200) {
          chapter.contents = res.data;
        } else {
          console.error(`获取章节 ${chapter.id} 内容失败：${res.msg}`);
          chapter.contents = [];
        }
      } catch (error) {
        console.error(`获取章节 ${chapter.id} 内容失败`, error);
        chapter.contents = [];
      }
    },

    // 切换详情全屏状态
    toggleDetailFullscreen() {
      this.isDetailFullscreen = !this.isDetailFullscreen;
    },

    // 行点击事件
    // handleRowClick(row) {
    //   this.showCourseDetail(row);
    // },

    // 添加章节
    addChapter() {
      this.chapterDialogTitle = "新增章节";
      this.chapterForm = {
        id: "",
        courseId: this.currentCourse.id,
        chapterName: "",
        description: "",
        isFree: 0,
        originalPrice: null,
        discountPrice: null,
        sort: 0,
      };
      this.chapterDialogVisible = true;
    },

    // 编辑章节
    editChapter(chapter) {
      this.chapterDialogTitle = "编辑章节";
      this.chapterForm = { ...chapter };
      this.chapterDialogVisible = true;
    },

    // 提交章节表单
    async submitChapterForm() {
      this.$refs.chapterForm.validate(async (valid) => {
        if (!valid) return;

        this.chapterSubmitLoading = true;

        try {
          let res;

          if (this.chapterForm.id) {
            // 编辑章节
            res = await this.$http.put(
              `/courseChapter/update/${this.chapterForm.id}`,
              this.chapterForm
            );
          } else {
            // 新增章节
            res = await this.$http.post("/courseChapter/add", this.chapterForm);
          }

          if (res.data.code === 200) {
            this.$message.success(
              this.chapterForm.id ? "章节更新成功" : "章节新增成功"
            );
            this.chapterDialogVisible = false;
            this.loadChaptersAndContents();
          } else {
            this.$message.error(
              res.data.msg ||
                (this.chapterForm.id ? "章节更新失败" : "章节新增失败")
            );
          }
        } catch (error) {
          this.$message.error(
            this.chapterForm.id
              ? "章节更新失败，请重试"
              : "章节新增失败，请重试"
          );
          console.error(error);
        } finally {
          this.chapterSubmitLoading = false;
        }
      });
    },

    // 删除章节
    async deleteChapter(chapterId) {
      try {
        const confirmResult = await this.$confirm(
          "确定要删除该章节吗？删除后章节下的所有内容也将被删除。",
          "删除确认",
          {
            confirmButtonText: "确定",
            cancelButtonText: "取消",
            type: "warning",
          }
        );

        if (confirmResult !== "confirm") return;

        const { data: res } = await this.$http.delete(
          `/courseChapter/delete/${chapterId}`
        );

        if (res.code === 200) {
          this.$message.success("章节删除成功");
          this.loadChaptersAndContents();
        } else {
          this.$message.error("章节删除失败：" + res.msg);
        }
      } catch (error) {
        if (error !== "cancel") {
          this.$message.error("章节删除失败，请重试");
          console.error(error);
        }
      }
    },

    // 添加章节内容
    addChapterContent(chapterId) {
      this.contentDialogTitle = "新增内容";
      this.contentForm = {
        id: "",
        chapterId,
        contentType: 1,
        contentTitle: "",
        textContent: "",
        videoUrl: "",
        videoDuration: 0,
        audioUrl: "",
        audioDuration: 0,
        sort: 0,
      };
      this.contentDialogVisible = true;
    },

    // 编辑章节内容
    editChapterContent(content) {
      this.contentDialogTitle = "编辑内容";
      this.contentForm = { ...content };
      this.contentDialogVisible = true;
    },

    // 内容类型改变
    handleContentTypeChange() {
      // 重置其他类型的内容
      if (this.contentForm.contentType !== 1) {
        this.contentForm.textContent = "";
      }
      if (this.contentForm.contentType !== 2) {
        this.contentForm.videoUrl = "";
        this.contentForm.videoDuration = 0;
      }
      if (this.contentForm.contentType !== 3) {
        this.contentForm.audioUrl = "";
        this.contentForm.audioDuration = 0;
      }
    },

    // 提交内容表单
    async submitContentForm() {
      this.$refs.contentForm.validate(async (valid) => {
        if (!valid) return;

        this.contentSubmitLoading = true;

        try {
          let res;

          if (this.contentForm.id) {
            // 编辑内容
            res = await this.$http.put(
              `/courseChapterContent/update/${this.contentForm.id}`,
              this.contentForm
            );
          } else {
            // 新增内容
            res = await this.$http.post(
              "/courseChapterContent/add",
              this.contentForm
            );
          }

          if (res.data.code === 200) {
            this.$message.success(
              this.contentForm.id ? "内容更新成功" : "内容新增成功"
            );
            this.contentDialogVisible = false;
            this.loadChaptersAndContents();
          } else {
            this.$message.error(
              res.data.msg ||
                (this.contentForm.id ? "内容更新失败" : "内容新增失败")
            );
          }
        } catch (error) {
          this.$message.error(
            this.contentForm.id
              ? "内容更新失败，请重试"
              : "内容新增失败，请重试"
          );
          console.error(error);
        } finally {
          this.contentSubmitLoading = false;
        }
      });
    },

    // 处理视频上传成功
    handleVideoUploadSuccess(response) {
      if (response.code === 200) {
        this.contentForm.videoUrl = response.data.url;
        this.$message.success("视频上传成功");
      } else {
        this.$message.error("视频上传失败：" + response.msg);
      }
    },

    // 视频上传前校验
    beforeVideoUpload(file) {
      const isVideo = file.type.indexOf("video/") === 0;
      const isLt100M = file.size / 1024 / 1024 < 100;

      if (!isVideo) {
        this.$message.error("上传文件必须是视频格式!");
      }
      if (!isLt100M) {
        this.$message.error("上传视频大小不能超过 100MB!");
      }

      return isVideo && isLt100M;
    },

    // 处理音频上传成功
    handleAudioUploadSuccess(response) {
      if (response.code === 200) {
        this.contentForm.audioUrl = response.data.url;
        this.$message.success("音频上传成功");
      } else {
        this.$message.error("音频上传失败：" + response.msg);
      }
    },

    // 音频上传前校验
    beforeAudioUpload(file) {
      const isAudio = file.type.indexOf("audio/") === 0;
      const isLt20M = file.size / 1024 / 1024 < 20;

      if (!isAudio) {
        this.$message.error("上传文件必须是音频格式!");
      }
      if (!isLt20M) {
        this.$message.error("上传音频大小不能超过 20MB!");
      }

      return isAudio && isLt20M;
    },

    // 删除章节内容
    async deleteChapterContent(contentId) {
      try {
        const confirmResult = await this.$confirm(
          "确定要删除该内容吗？",
          "删除确认",
          {
            confirmButtonText: "确定",
            cancelButtonText: "取消",
            type: "warning",
          }
        );

        if (confirmResult !== "confirm") return;

        const { data: res } = await this.$http.delete(
          `/courseChapterContent/delete/${contentId}`
        );

        if (res.code === 200) {
          this.$message.success("内容删除成功");
          this.loadChaptersAndContents();
        } else {
          this.$message.error("内容删除失败：" + res.msg);
        }
      } catch (error) {
        if (error !== "cancel") {
          this.$message.error("内容删除失败，请重试");
          console.error(error);
        }
      }
    },

    // 删除课程
    async handleDelete(courseId) {
      try {
        const confirmResult = await this.$confirm(
          "确定要删除该课程吗？删除后课程的所有章节和内容也将被删除。",
          "删除确认",
          {
            confirmButtonText: "确定",
            cancelButtonText: "取消",
            type: "warning",
          }
        );

        if (confirmResult !== "confirm") return;

        const { data: res } = await this.$http.delete(
          `/course/delete/${courseId}`
        );

        if (res.code === 200) {
          this.$message.success("课程删除成功");
          this.loadCourses();
        } else {
          this.$message.error("课程删除失败：" + res.msg);
        }
      } catch (error) {
        if (error !== "cancel") {
          this.$message.error("课程删除失败，请重试");
          console.error(error);
        }
      }
    },

    // 格式化时长（秒 -> mm:ss）
    formatDuration(seconds) {
      if (!seconds || seconds < 0) return "00:00";

      const mins = Math.floor(seconds / 60);
      const secs = seconds % 60;

      return `${mins.toString().padStart(2, "0")}:${secs
        .toString()
        .padStart(2, "0")}`;
    },
  },
};
</script>

<style scoped>
.breadcrumb {
  padding: 15px 20px;
  background-color: #f5f7fa;
  border-radius: 4px;
  margin-bottom: 20px;
}

.main-card {
  box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.05);
  border-radius: 4px;
  overflow: hidden;
}

.search-and-actions {
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  align-items: center;
  padding: 15px 20px;
  border-bottom: 1px solid #f2f2f2;
}

.search-form {
  width: calc(100% - 120px);
}

.action-buttons {
  margin-top: 10px;
}

@media (min-width: 768px) {
  .action-buttons {
    margin-top: 0;
  }
}

.el-table {
  margin-top: 15px;
}

.pagination {
  margin: 15px 0;
  text-align: right;
}

.course-name-wrapper {
  display: flex;
  align-items: center;
}

.course-cover {
  width: 40px;
  height: 22.5px;
  object-fit: cover;
  border-radius: 3px;
  margin-right: 8px;
}

.free-tag {
  display: inline-block;
  background-color: #f0f9eb;
  color: #52c41a;
  padding: 2px 5px;
  border-radius: 3px;
  font-size: 12px;
}

.price-wrapper {
  display: flex;
  align-items: baseline;
}

.discount-price {
  color: #f5222d;
  font-weight: bold;
  margin-right: 5px;
}

.original-price {
  color: #8c8c8c;
  text-decoration: line-through;
  font-size: 12px;
}

.course-form {
  margin-top: 15px;
}

.avatar-uploader .el-upload {
  border: 1px dashed #d9d9d9;
  border-radius: 6px;
  cursor: pointer;
  position: relative;
  overflow: hidden;
  transition: all 0.3s;
}

.avatar-uploader .el-upload:hover {
  border-color: #409eff;
}

.avatar-uploader-icon {
  font-size: 28px;
  color: #8c8c8c;
  width: 360px;
  height: 202px;
  line-height: 202px;
  text-align: center;
}

.cover-image {
  width: 100%;
  height: 202px;
  display: block;
  object-fit: cover;
}

.upload-tip {
  margin-top: 8px;
  font-size: 12px;
  color: #8c8c8c;
}

.intro-preview {
  margin-top: 10px;
  padding: 10px;
  border: 1px solid #e8e8e8;
  border-radius: 4px;
}

.preview-label {
  font-weight: bold;
  margin-bottom: 5px;
  display: block;
}

.preview-content {
  font-size: 14px;
  color: #333;
  line-height: 1.5;
}

.course-detail {
  max-height: calc(100vh - 200px);
  overflow-y: auto;
  padding-right: 10px;
}

.detail-header {
  display: flex;
  flex-wrap: wrap;
  margin-bottom: 20px;
  padding-bottom: 20px;
  border-bottom: 1px solid #f2f2f2;
}

.detail-cover {
  width: 320px;
  height: 180px;
  object-fit: cover;
  border-radius: 6px;
  margin-right: 20px;
  flex-shrink: 0;
}

.detail-info {
  flex: 1;
  min-width: 300px;
}

.detail-title {
  font-size: 20px;
  margin-bottom: 15px;
  color: #333;
}

.detail-meta {
  margin-bottom: 15px;
  display: flex;
  flex-wrap: wrap;
  gap: 15px;
}

.meta-item {
  color: #666;
  font-size: 14px;
  display: flex;
  align-items: center;
}

.meta-item i {
  margin-right: 5px;
  color: #409eff;
}

.detail-price {
  margin-bottom: 15px;
}

.detail-free {
  display: inline-block;
  background-color: #f0f9eb;
  color: #52c41a;
  padding: 5px 10px;
  border-radius: 4px;
  font-size: 16px;
  font-weight: bold;
}

.detail-discount {
  color: #f5222d;
  font-size: 18px;
  font-weight: bold;
}

.detail-original {
  color: #8c8c8c;
  text-decoration: line-through;
  margin-left: 10px;
}

.detail-status {
  margin-bottom: 15px;
}

.intro-content {
  line-height: 1.8;
  color: #333;
  font-size: 15px;
  padding: 10px 0;
}

.chapters-management {
  padding: 10px 0;
}

.chapters-list {
  margin-top: 15px;
}

.chapter-item {
  border: 1px solid #e8e8e8;
  border-radius: 4px;
  margin-bottom: 15px;
  overflow: hidden;
}

.chapter-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  background-color: #f5f7fa;
  padding: 12px 15px;
  border-bottom: 1px solid #e8e8e8;
}

.chapter-title {
  display: flex;
  align-items: center;
  font-weight: bold;
  color: #333;
}

.chapter-title i {
  margin-right: 8px;
  color: #409eff;
}

.chapter-contents {
  padding: 10px 15px;
}

.content-item {
  display: flex;
  align-items: center;
  padding: 10px 0;
  border-bottom: 1px dashed #e8e8e8;
}

.content-item:last-child {
  border-bottom: none;
}

.content-icon {
  width: 30px;
  text-align: center;
  color: #409eff;
  margin-right: 15px;
}

.content-info {
  flex: 1;
}

.content-title {
  margin-bottom: 5px;
  color: #333;
}

.content-meta {
  display: flex;
  align-items: center;
  font-size: 12px;
  color: #8c8c8c;
}

.meta-text {
  margin-right: 15px;
}

.free-badge {
  display: inline-block;
  background-color: #f0f9eb;
  color: #52c41a;
  padding: 1px 5px;
  border-radius: 3px;
  font-size: 12px;
}

.no-content,
.no-chapter {
  text-align: center;
  padding: 20px;
  color: #8c8c8c;
  display: flex;
  align-items: center;
  justify-content: center;
}

.no-content i,
.no-chapter i {
  margin-right: 8px;
}

.video-uploader,
.audio-uploader {
  border: 1px dashed #d9d9d9;
  border-radius: 6px;
  padding: 20px;
  text-align: center;
  cursor: pointer;
  transition: all 0.3s;
}

.video-uploader:hover,
.audio-uploader:hover {
  border-color: #409eff;
}

.upload-placeholder {
  color: #8c8c8c;
  padding: 30px 0;
}

.upload-placeholder i {
  font-size: 48px;
  margin-bottom: 10px;
  display: block;
}

.video-preview {
  max-width: 100%;
  max-height: 300px;
  border-radius: 4px;
}

.audio-preview {
  width: 100%;
  margin-top: 10px;
}
</style>
